export type CsvOptions = {
  /**
   * The delimiter character to use
   */
  delimiterChar?: string

  /**
   * The quote character to use
   */
  quoteChar?: string

  /**
   * The escape character to use
   */
  escapeChar?: string

  /**
   * The newline character to use
   */
  newlineChar?: string
}

/**
 * Options for encoding CSV data
 */
export type CsvEncodeOptions = {
  /**
   * Whether to include headers (first row in an array of arrays, keys of first row
   * in an array of objects) in the output
   * If an array of keys is pasesd, they will be used as headers
   */
  headers?: boolean | string[]
}

/**
 * Options for decoding CSV data
 */
export type CsvDecodeOptions = {
  /**
   * Header options
   * false: Treat first row as data
   * true: Treat first row as keys, decoding results as objects
   * string[]: Treat first row as data, decoding results as objects with the given keys
   */
  headers?: boolean | string[]
}

export class Csv {
  #delimiterChar: string
  #quoteChar: string
  #escapeChar: string
  #newlineChar: string

  constructor({
    delimiterChar = ',',
    quoteChar = '"',
    escapeChar = '"',
    newlineChar = '\n',
  }: CsvOptions = {}) {
    this.#delimiterChar = delimiterChar
    this.#quoteChar = quoteChar
    this.#escapeChar = escapeChar
    this.#newlineChar = newlineChar
  }

  #encodeItem(item: string) {
    if (
      item.includes(this.#delimiterChar) ||
      item.includes(this.#quoteChar) ||
      item.includes(this.#newlineChar)
    ) {
      return (
        this.#quoteChar +
        item.replaceAll(this.#quoteChar, this.#escapeChar + this.#quoteChar) +
        this.#quoteChar
      )
    }
    return item
  }

  #stringifyItem(value: any): string {
    if (typeof value === 'object' && value !== null) {
      return JSON.stringify(value)
    }

    return String(value)
  }

  /**
   * Encode a row array to a CSV line (without trailing newline character)
   */
  encodeRow<T extends any[]>(line: T) {
    return line.map(item => this.#encodeItem(this.#stringifyItem(item))).join(this.#delimiterChar)
  }

  /**
   * Encode the given data into CSV
   * Supports both array of arrays and array of objects
   */
  encode(data: any[], { headers = true }: CsvEncodeOptions = {}) {
    if (!Array.isArray(data)) {
      data = [data]
    }

    if (data.length === 0) return ''

    // Be lenient and allow single-line data to be passed
    if (typeof data[0] !== 'object' || data[0] === null) {
      data = [data]
    }

    data = [...data]

    const firstLine = data[0]

    if (typeof firstLine === 'object' && firstLine !== null && !Array.isArray(firstLine)) {
      const csvHeaders = Array.isArray(headers) ? headers : Object.keys(firstLine)
      data = data.map(line => csvHeaders.map(header => line[header]))

      if (headers === true) {
        data.unshift(csvHeaders)
      }
    } else if (Array.isArray(headers)) {
      data.unshift(headers)
    }

    return data.map(line => this.encodeRow(line)).join(this.#newlineChar)
  }

  /**
   * Decode the given CSV string into an array of arrays
   * Omitting the last newline character is allowed
   */
  decodeLines<T extends string[][]>(source: string): string[][] {
    let inQuote = false
    let inEscape = false
    let currentItem = ''
    let line: string[] = []

    const lines: string[][] = []

    for (const char of source) {
      if (inQuote) {
        if (inEscape) {
          currentItem += char
          inEscape = false
        } else if (char === this.#quoteChar) {
          inQuote = false
        } else if (char === this.#escapeChar) {
          inEscape = true
        } else {
          currentItem += char
        }
      } else {
        switch (char) {
          case this.#delimiterChar: {
            line.push(currentItem)
            currentItem = ''
            break
          }

          case this.#quoteChar: {
            inQuote = true
            break
          }

          case this.#newlineChar: {
            line.push(currentItem)
            lines.push(line)
            line = []
            currentItem = ''
            break
          }

          default: {
            currentItem += char
          }
        }
      }
    }

    if (currentItem.length > 0) line.push(currentItem)
    if (line.length > 0) lines.push(line)

    return lines as T
  }

  /**
   * Decode the given CSV string into an array of arrays or objects
   */
  decode<T = any>(encoded: string, { headers = true }: CsvDecodeOptions = {}): T[] {
    let lines = this.decodeLines(encoded)

    if (headers === false) {
      return lines as T[]
    }

    let keys: string[]
    if (headers === true) {
      keys = lines[0]
      lines = lines.slice(1)
    } else {
      keys = headers
    }

    return lines.map(line =>
      Object.fromEntries(keys.map((key, index) => [key, line[index]])),
    ) as T[]
  }
}
